/**
 * EditTrackingContext
 * Feature: 015-manual-crud-entities - User Story 4
 * 
 * React context for tracking field changes during edit operations
 * Provides visual indicators for modified fields
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface EditTrackingContextValue {
  changedFields: Set<string>;
  markFieldChanged: (fieldName: string) => void;
  clearChangedFields: () => void;
  hasChanges: boolean;
}

const EditTrackingContext = createContext<EditTrackingContextValue | undefined>(undefined);

export const useEditTracking = () => {
  const context = useContext(EditTrackingContext);
  if (!context) {
    throw new Error('useEditTracking must be used within EditTrackingProvider');
  }
  return context;
};

export const useOptionalEditTracking = () => useContext(EditTrackingContext);

export const EditTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());

  const markFieldChanged = useCallback((fieldName: string) => {
    setChangedFields((prev) => {
      // Only update if field is not already in the Set
      if (prev.has(fieldName)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(fieldName);
      return next;
    });
  }, []);

  const clearChangedFields = useCallback(() => {
    setChangedFields(new Set());
  }, []);

  const value: EditTrackingContextValue = useMemo(() => ({
    changedFields,
    markFieldChanged,
    clearChangedFields,
    hasChanges: changedFields.size > 0,
  }), [changedFields, markFieldChanged, clearChangedFields]);

  return <EditTrackingContext.Provider value={value}>{children}</EditTrackingContext.Provider>;
};
